import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
    Radio,
    Plus,
    Settings,
    Trash2,
    Check,
    X,
    ExternalLink
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform configs
const PLATFORMS = {
    youtube: { name: 'YouTube', color: 'bg-red-500', textColor: 'text-red-500', bgLight: 'bg-red-500/10' },
    facebook: { name: 'Facebook', color: 'bg-blue-600', textColor: 'text-blue-600', bgLight: 'bg-blue-600/10' },
    twitter: { name: 'X (Twitter)', color: 'bg-sky-500', textColor: 'text-sky-500', bgLight: 'bg-sky-500/10' },
    linkedin: { name: 'LinkedIn', color: 'bg-blue-700', textColor: 'text-blue-700', bgLight: 'bg-blue-700/10' },
    twitch: { name: 'Twitch', color: 'bg-purple-500', textColor: 'text-purple-500', bgLight: 'bg-purple-500/10' },
    tiktok: { name: 'TikTok', color: 'bg-pink-500', textColor: 'text-pink-500', bgLight: 'bg-pink-500/10' },
    whatsapp: { name: 'WhatsApp', color: 'bg-green-500', textColor: 'text-green-500', bgLight: 'bg-green-500/10' },
    meet: { name: 'Google Meet', color: 'bg-emerald-500', textColor: 'text-emerald-500', bgLight: 'bg-emerald-500/10' },
    discord: { name: 'Discord', color: 'bg-indigo-500', textColor: 'text-indigo-500', bgLight: 'bg-indigo-500/10' },
    telegram: { name: 'Telegram', color: 'bg-sky-400', textColor: 'text-sky-400', bgLight: 'bg-sky-400/10' },
    slack: { name: 'Slack', color: 'bg-amber-500', textColor: 'text-amber-500', bgLight: 'bg-amber-500/10' },
    zoom: { name: 'Zoom', color: 'bg-blue-500', textColor: 'text-blue-500', bgLight: 'bg-blue-500/10' },
};

const PlatformIcon = ({ platform, className }) => {
    const icons = {
        youtube: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
        ),
        facebook: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
        ),
        twitter: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
        ),
        linkedin: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
        ),
        twitch: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
            </svg>
        ),
        tiktok: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
            </svg>
        ),
        whatsapp: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
        ),
        meet: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C6.28 0 1.636 4.644 1.636 10.364c0 2.013.578 3.891 1.575 5.478L.545 24l8.389-2.636c1.538.89 3.32 1.4 5.066 1.4C19.72 22.764 24 18.084 24 12.364 24 5.644 18.72 0 12 0zm0 2.182c5.35 0 9.818 4.468 9.818 10.182 0 4.735-3.508 8.727-8.182 9.545l-.364.073-5.49 1.727 1.8-5.236-.29-.436A9.07 9.07 0 012.182 10.364c0-5.35 4.468-9.818 9.818-9.818zm-3.273 5.454v8.728h1.637V13.09h2.181l2.728 3.273h2.036l-2.945-3.49c1.4-.437 2.4-1.691 2.4-3.237 0-1.855-1.509-3.273-3.491-3.273H8.727v1.273zm1.637 1.273h2.909c1.018 0 1.854.69 1.854 1.636 0 .946-.836 1.637-1.854 1.637H10.364V8.909z"/>
            </svg>
        ),
        discord: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/>
            </svg>
        ),
        telegram: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
        ),
        slack: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
            </svg>
        ),
        zoom: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M4.585 7.588a1.5 1.5 0 0 0-1.5 1.5v5.823a1.5 1.5 0 0 0 1.5 1.5h9.168a1.5 1.5 0 0 0 1.5-1.5V13.5l4.162 2.774a.75.75 0 0 0 1.17-.624V8.35a.75.75 0 0 0-1.17-.624l-4.162 2.774V9.088a1.5 1.5 0 0 0-1.5-1.5H4.585z"/>
            </svg>
        )
    };
    return icons[platform] || <Radio className={className} />;
};

export const Broadcasts = () => {
    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [newDestination, setNewDestination] = useState({
        platform: '',
        name: '',
        stream_key: '',
        rtmp_url: ''
    });

    useEffect(() => {
        fetchDestinations();
    }, []);

    const fetchDestinations = async () => {
        try {
            const res = await axios.get(`${API}/destinations`);
            setDestinations(res.data);
        } catch (error) {
            console.error('Error fetching destinations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (destId) => {
        try {
            await axios.put(`${API}/destinations/${destId}/toggle`);
            fetchDestinations();
        } catch (error) {
            console.error('Error toggling destination:', error);
            toast.error('Failed to toggle destination');
        }
    };

    const handleDelete = async (destId) => {
        try {
            await axios.delete(`${API}/destinations/${destId}`);
            toast.success('Destination removed');
            fetchDestinations();
        } catch (error) {
            console.error('Error deleting destination:', error);
            toast.error('Failed to remove destination');
        }
    };

    const handleAddDestination = async () => {
        if (!newDestination.platform || !newDestination.name) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            await axios.post(`${API}/destinations`, newDestination);
            toast.success('Destination added');
            setIsAddDialogOpen(false);
            setNewDestination({ platform: '', name: '', stream_key: '', rtmp_url: '' });
            fetchDestinations();
        } catch (error) {
            console.error('Error adding destination:', error);
            toast.error('Failed to add destination');
        }
    };

    const connectedCount = destinations.filter(d => d.is_connected).length;

    return (
        <div className="space-y-6" data-testid="broadcasts-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold">Broadcast Destinations</h1>
                    <p className="text-muted-foreground mt-1">
                        {connectedCount} of {destinations.length} platforms connected
                    </p>
                </div>
                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="gap-2" data-testid="add-destination-btn">
                            <Plus className="w-4 h-4" />
                            Add Destination
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Add Broadcast Destination</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                            <div>
                                <label className="text-sm font-medium mb-2 block">Platform</label>
                                <Select
                                    value={newDestination.platform}
                                    onValueChange={(value) => setNewDestination({ ...newDestination, platform: value })}
                                >
                                    <SelectTrigger data-testid="platform-select">
                                        <SelectValue placeholder="Select platform" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(PLATFORMS).map(([key, config]) => (
                                            <SelectItem key={key} value={key}>
                                                <div className="flex items-center gap-2">
                                                    <PlatformIcon platform={key} className="w-4 h-4" />
                                                    {config.name}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-2 block">Display Name</label>
                                <Input
                                    value={newDestination.name}
                                    onChange={(e) => setNewDestination({ ...newDestination, name: e.target.value })}
                                    placeholder="e.g., My YouTube Channel"
                                    data-testid="destination-name-input"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-2 block">Stream Key (optional)</label>
                                <Input
                                    value={newDestination.stream_key}
                                    onChange={(e) => setNewDestination({ ...newDestination, stream_key: e.target.value })}
                                    placeholder="xxxx-xxxx-xxxx-xxxx"
                                    type="password"
                                    data-testid="stream-key-input"
                                />
                            </div>
                            <Button onClick={handleAddDestination} className="w-full" data-testid="save-destination-btn">
                                Add Destination
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Destinations Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {loading ? (
                    [1, 2, 3].map((i) => (
                        <Card key={i} className="animate-pulse">
                            <CardContent className="p-6">
                                <div className="h-20 bg-muted rounded" />
                            </CardContent>
                        </Card>
                    ))
                ) : (
                    destinations.map((dest) => {
                        const config = PLATFORMS[dest.platform] || { name: dest.platform, color: 'bg-gray-500', textColor: 'text-gray-500', bgLight: 'bg-gray-500/10' };
                        
                        return (
                            <Card key={dest.id} className="overflow-hidden" data-testid={`destination-card-${dest.id}`}>
                                <CardContent className="p-0">
                                    <div className={`h-2 ${config.color}`} />
                                    <div className="p-5">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-12 h-12 rounded-xl ${config.bgLight} ${config.textColor} flex items-center justify-center`}>
                                                    <PlatformIcon platform={dest.platform} className="w-6 h-6" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold">{dest.name}</h3>
                                                    <p className="text-sm text-muted-foreground">{config.name}</p>
                                                </div>
                                            </div>
                                            <Switch
                                                checked={dest.is_enabled}
                                                onCheckedChange={() => handleToggle(dest.id)}
                                                disabled={!dest.is_connected}
                                            />
                                        </div>
                                        
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                {dest.is_connected ? (
                                                    <span className="flex items-center gap-1 text-sm text-green-600">
                                                        <Check className="w-4 h-4" />
                                                        Connected
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                                                        <X className="w-4 h-4" />
                                                        Not connected
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    data-testid={`settings-${dest.id}`}
                                                >
                                                    <Settings className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                    onClick={() => handleDelete(dest.id)}
                                                    data-testid={`delete-${dest.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })
                )}
            </div>

            {/* Help Section */}
            <Card className="bg-gradient-to-r from-primary/5 to-purple-500/5">
                <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">Need help connecting?</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        Learn how to find your stream keys and connect your platforms to StreamHub.
                    </p>
                    <Button variant="outline" size="sm" className="gap-2">
                        View Documentation
                        <ExternalLink className="w-4 h-4" />
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
};

export default Broadcasts;
